import React, { useEffect, useState } from 'react';
import './css/style.scss';
import NavBar from '../navbar/navbar';
import '../../theme.css';
import '../../index.css';
import PerformanceHighlights from './widget-performance-highlights';
import BusinessNarratives from './widget-business-narratives';
import WidgetAlerts from './widget-alerts';
import WidgetRecommendedActions from './widget-recommended-actions';
import WidgetMyReports from './widget-my-reports';

// Theme
const theme = {
  cardBoxShadow: '0px 2px 4px rgba(0, 0, 0, 0.06)',
  cardBackgroundPrimaryColor: '#fff',
};

const WidgetCard = ({ children, image, style }) => {
  const cardStyle = {
    padding: '24px',
    textAlign: 'center',
    borderRadius: '24px',
    boxShadow: theme.cardBoxShadow,
    background: theme.cardBackgroundPrimaryColor,
    ...style,
  };

  if (image) {
    cardStyle.backgroundImage = `url(${image})`;
    cardStyle.backgroundSize = 'cover';
    cardStyle.backgroundPosition = 'center';
  }

  return <div style={cardStyle}>{children}</div>;
};

// Skeleton loader
const WidgetSkeleton = ({ minHeight }) => (
  <>
    <div
      style={{
        height: '32px',
        width: '60%',
        backgroundColor: '#f0f0f0',
        borderRadius: '4px',
        marginBottom: '8px',
      }}
    ></div>
    <div
      style={{
        height: '16px',
        width: '80%',
        backgroundColor: '#f0f0f0',
        borderRadius: '4px',
        marginBottom: '8px',
      }}
    ></div>
    <div
      style={{
        height: '16px',
        width: '90%',
        backgroundColor: '#f0f0f0',
        borderRadius: '4px',
        marginBottom: '16px',
      }}
    ></div>
    <div
      style={{
        height: `${minHeight - 140}px`,
        width: '100%',
        backgroundColor: '#f0f0f0',
        borderRadius: '8px',
      }}
    ></div>
  </>
);

const LaunchPad = () => {
  const [loading, setLoading] = useState(true);
  const [showExit, setShowExit] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Simulating the exit animation logic
  useEffect(() => {
    const handlePopState = () => {
      setShowExit(true);
      setTimeout(() => {
        // Here you would navigate, for example: window.history.back();
      }, 500); // Duration of the exit animation
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Renderer for each widget
  const renderWidget = (widget_uuid, min_height, min_width, Component, extraProps = {}, image = null) => (
    <WidgetCard
      key={widget_uuid}
      style={{ height: min_height }}
      image={image && !loading ? image : null}
    >
      {loading ? (
        <WidgetSkeleton minHeight={min_height} />
      ) : (
        <Component
          key={widget_uuid}
          minHeight={min_height}
          minWidth={min_width}
          borderRadius={8}
          {...extraProps}
        />
      )}
    </WidgetCard>
  );

  return (
    <div className="launchpad">
      <NavBar />
      <div
        style={{
          paddingTop: '50px',
          paddingRight: '15px',
          paddingLeft: '15px',
          paddingBottom: '15px',
          overflowX: 'hidden',
          overflowY: 'auto',
          width: '100%',
          boxSizing: 'border-box',
          margin: 0,
        }}
      >
        {/* 3-column layout */}
        <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
          {/* Left column: Business Narratives */}
          <div style={{ flex: 1 }}>
            {renderWidget(
              'business-narratives',
              905,
              562,
              BusinessNarratives,
              {},
              '/martin-martz-aQwhePkJiEw-unsplash.png'
            )}
          </div>

          {/* Middle column: Performance Highlights + Recommended Actions */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {renderWidget('performance-highlights', 535, 448, PerformanceHighlights)}
            {renderWidget('recommended-actions', 357, 448, WidgetRecommendedActions, {
              sections: [
                { id: '1', key: 'actions', display_name: 'Actions', apiEndpoint: '' },
                { id: '2', key: 'boards', display_name: 'Boards', apiEndpoint: '' },
                { id: '3', key: 'prompts', display_name: 'Prompts', apiEndpoint: '' },
              ],
            })}
          </div>

          {/* Right column: Alerts + My Reports */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {renderWidget('alerts', 645, 334, WidgetAlerts)}
            {renderWidget('my-reports', 244, 334, WidgetMyReports)}
          </div>
        </div>
      </div>

      {/* Page transition */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#fff',
          transformOrigin: 'left',
          transform: 'scaleX(0)',
          transition: 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
          zIndex: 999,
          // Inline styles to replicate Framer Motion's logic
          // The "enter" animation is 'scaleX(0)' which is the final state.
          // The initial state for the enter animation is implicit.
          // The exit animation is handled by showExit state
          ... (showExit && {
            transform: 'scaleX(1)',
            transformOrigin: 'right',
            transition: 'transform 0.5s cubic-bezier(0.55, 0.055, 0.675, 0.19)',
          }),
        }}
        className="privacy-screen"
      />
    </div>
  );
};

export default LaunchPad;